import { useState } from 'react';

import { useCartActions, useCartValues } from '@fhs/cart/src/state/cart-store';
import { ICartEntry, IItem } from '@rbi-ctg/menu';
import {
  ClickEventComponentNames,
  CustomEventNames,
  EventTypes,
  useCRMEventsContext,
} from 'state/crm-events';
import { useMenuOptionsContext } from 'state/menu-options';
import { createCartEntry } from 'utils/cart';

type useDonationProps = {
  donationItems: IItem[];
};

function useDonation({ donationItems }: useDonationProps) {
  const { cartEntries } = useCartValues();
  const { addItemToCart, removeItemFromCart } = useCartActions();
  const { logRBIEvent } = useCRMEventsContext();

  const { pricingFunction } = useMenuOptionsContext();

  const [selectedDonationCartEntry, setSelectedDonationCartEntry] = useState(() => {
    const preloadedDonationCartEntry = cartEntries?.find(cartEntry => cartEntry.isDonation);
    if (preloadedDonationCartEntry && donationItems) {
      const isPreloadedDonationCartEntryValid = donationItems.some(
        item => item._id === preloadedDonationCartEntry._id
      );
      if (isPreloadedDonationCartEntryValid) {
        return preloadedDonationCartEntry;
      }
      removeItemFromCart({ cartId: preloadedDonationCartEntry.cartId });
    }
    return null;
  });

  function handleAddDonationCartEntry(donationItem: IItem) {
    if (selectedDonationCartEntry?._id === donationItem._id) {
      return;
    }

    if (selectedDonationCartEntry) {
      removeItemFromCart({ cartId: selectedDonationCartEntry.cartId });
    }

    const newDonationCartEntry: ICartEntry = {
      ...createCartEntry({
        item: donationItem,
        price: pricingFunction(donationItem),
      }),
      isDonation: true,
    };
    addItemToCart(newDonationCartEntry);
    setSelectedDonationCartEntry(newDonationCartEntry);

    logRBIEvent({
      name: CustomEventNames.CLICK_EVENT,
      type: EventTypes.Other,
      attributes: {
        component: ClickEventComponentNames.DONATION,
        text: `Add Donation - ${donationItem.name?.locale || 'Unknown'}`,
        componentId: donationItem._id,
        action: 'add',
      },
    });
  }

  function handleRemoveDonationCartEntry({ cartId }: { cartId: string }) {
    const donationToRemove = selectedDonationCartEntry;
    removeItemFromCart({ cartId });
    setSelectedDonationCartEntry(null);

    if (donationToRemove) {
      logRBIEvent({
        name: CustomEventNames.CLICK_EVENT,
        type: EventTypes.Other,
        attributes: {
          component: ClickEventComponentNames.DONATION,
          text: `Remove Donation - ${donationToRemove.name || 'Unknown'}`,
          componentId: donationToRemove._id,
          action: 'remove',
        },
      });
    }
  }

  return {
    selectedDonationCartEntry,
    handleAddDonationCartEntry,
    handleRemoveDonationCartEntry,
  };
}

export default useDonation;
